import { DateTime } from "luxon";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyViewCustomerPlantGraphsAdminQuery,
  useViewCustomerPlantStorageDataMutation,
} from "../../../../redux/api/generation/generationAPI";
import {
  selectSiteChartFilter,
  setSiteChartFilter,
  SiteChartFilter,
} from "../../../../redux/features/site-chart-filter/site-chart-filter-slice";
import ChartLoadingOverlay from "../../../../shared/components/chart-loading-overlay/chart-loading-overlay";
import IViewCustomerSolarPlantGraphRequestDTO from "../../../../shared/oversight-core/dtos/request-dtos/view-customer-solar-plant-graph-request-dto";
import IViewCustomerPlantStorageDataResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/view-customer-plant-storage-data-response-dto";
import IViewCustomerSolarPlantGraphResponseDTO from "../../../../shared/oversight-core/dtos/response-dtos/view-customer-solar-plant-graph-response-dto";
import { EPlantStatus } from "../../../../shared/oversight-core/enums/plant-status";
import { ESolarPlantGraphType } from "../../../../shared/oversight-core/enums/solar-plant-graph-type";
import { ESolarPlantViewType } from "../../../../shared/oversight-core/enums/solar-plant-view-type";
import { IGroWattSolarPlantGraph } from "../../../../shared/oversight-core/interfaces/gro-watt-solar-plant-graph";
import { IGroWattSolarPlantView } from "../../../../shared/oversight-core/interfaces/gro-watt-solar-plant-view";
import { ISolarPlantStorageDataView } from "../../../../shared/oversight-core/interfaces/solar-plant-storage-data-view";
import LeafletMapDetails from "../../../../shared/oversight-core/shared-components/leaflet-map-details/leaflet-map-details";
import SolarInfoCard from "../../../../shared/oversight-core/shared-components/solar-info-card/solar-info-card";
import WetherInfo from "../../../../shared/oversight-core/shared-components/wether-info/wether-info";
import AppBannerWithIcon from "../../../../shared/oversight-core/ui-elements/app-banner-with-icon/app-banner-with-icon";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import BarChart from "../../../../shared/oversight-core/ui-elements/bar-chart/bar-chart";
import ArrowIconsButton from "../../../../shared/oversight-core/ui-elements/buttons/arrow-icons-button/arrow-icons-button";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import MultipleChartType from "../../../../shared/oversight-core/ui-elements/multiple-chart-type/multiple-chart-type";
import SpinnerModal from "../../../../shared/oversight-core/ui-elements/spinner/spinner";
import { formatDateByZoneId } from "../../../../shared/oversight-core/utils/date-utils";
import Helper from "../../../../shared/oversight-core/utils/helpers";
import GroWattEnergyFlow from "./energy-flow/gro-watt-energy-flow";
import VrmSiteDashboardFilters from "./site-dashboard-filters/vrm-site-dashboard-filters";
import styles from "./site-dashboard.module.scss";

interface IProps {
  plantId: string;
  solarPlant: IGroWattSolarPlantView;
}

const graphTypeOptions: Option[] = [
  { value: ESolarPlantGraphType.LIVE_FEED, label: "System Overview" },
  { value: ESolarPlantGraphType.CONSUMPTION, label: "Consumption" },
  { value: ESolarPlantGraphType.SOLAR_YIELD, label: "Solar" },
  { value: ESolarPlantGraphType.GRID, label: "Grid" },
];

const GroWattSiteDashboard = (props: IProps) => {
  const { plantId, solarPlant } = props;

  const selectChartFilter: SiteChartFilter = useSelector(selectSiteChartFilter);
  const dispatch = useDispatch();

  const zoneId = solarPlant?.timezone;

  const [selectedGraphType, setSelectedGraphType] = useState<Option>(
    graphTypeOptions[0],
  );
  const [showSiteViewDashboardFilters, setShowSiteViewDashboardFilters] =
    useState(false);
  const [quickFilter, setQuickFilter] = useState<Option>({
    value: ESolarPlantViewType.TODAY,
    label: "Today",
  });
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [graphData, setGraphData] = useState<IGroWattSolarPlantGraph>();
  const [storagePowerData, setStoragePowerData] = useState<
    ISolarPlantStorageDataView[]
  >([]);
  const [isHideAlerts, setIsHideAlerts] = useState(false);
  const [isFirstStorageLoad, setIsFirstStorageLoad] = useState(true);

  const [
    triggerCustomerPlantGraphs,
    { isFetching: isFetchingCustomerPlantGraphs },
  ] = useLazyViewCustomerPlantGraphsAdminQuery();

  const [
    triggerCustomerPlantStorageData,
    { isLoading: isFetchingCustomerPlantStorageData },
  ] = useViewCustomerPlantStorageDataMutation();

  useEffect(() => {
    if (plantId) {
      triggerCustomerPlantStorageData({ plantId, deviceSn: "" })
        .unwrap()
        .then((res: IViewCustomerPlantStorageDataResponseDTO) => {
          if (res.storageData) {
            setStoragePowerData(res.storageData);
          }
        })
        .catch(() => {
          setStoragePowerData([]);
        })
        .finally(() => {
          setIsFirstStorageLoad(false);
        });
    }
  }, [triggerCustomerPlantStorageData, plantId]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (plantId) {
        triggerCustomerPlantStorageData({ plantId, deviceSn: "" })
          .unwrap()
          .then((res: IViewCustomerPlantStorageDataResponseDTO) => {
            if (res.storageData) {
              setStoragePowerData(res.storageData);
            }
          })
          .catch(() => {
            setStoragePowerData([]);
          });
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [triggerCustomerPlantStorageData, plantId]);

  useEffect(() => {
    setStartDate(new Date(selectChartFilter.startDate));
    setEndDate(new Date(selectChartFilter.endDate));
  }, [selectChartFilter]);

  useEffect(() => {
    const filterParams: IViewCustomerSolarPlantGraphRequestDTO = {
      plantId: plantId,
      viewType: selectChartFilter.viewType
        ? selectChartFilter.viewType
        : (quickFilter.value as ESolarPlantViewType),
      graphType: selectedGraphType.value as ESolarPlantGraphType,
    };

    if (selectChartFilter.isFilterCustom) {
      filterParams.startDate = formatDateByZoneId(
        selectChartFilter.startDate,
        zoneId,
      );
      filterParams.endDate = formatDateByZoneId(
        selectChartFilter.endDate,
        zoneId,
      );
    }

    if (plantId) {
      triggerCustomerPlantGraphs({ ...filterParams })
        .unwrap()
        .then((res: IViewCustomerSolarPlantGraphResponseDTO) => {
          const data = res.graphData as IGroWattSolarPlantGraph;
          setGraphData(data);
        })
        .catch(() => {
          setGraphData(undefined);
        });
    }
  }, [
    triggerCustomerPlantGraphs,
    plantId,
    selectChartFilter,
    selectedGraphType,
    quickFilter,
    zoneId,
  ]);

  const getGraphTimeStamps = useMemo(() => {
    if (!graphData || !graphData.points) return [];
    return graphData.points.map((p) => new Date(p.bucketStart).getTime());
  }, [graphData]);

  const getConsumptionGraphData = useMemo(() => {
    if (!graphData || !graphData.points) return [];

    return graphData.points.map((p) => {
      return Number(Helper.roundTo2(p.loadWh / 1000));
    });
  }, [graphData]);

  const getSolarGraphData = useMemo(() => {
    if (!graphData || !graphData.points) return [];

    return graphData.points.map((p) => {
      return Number(Helper.roundTo2(p.energyWh / 1000));
    });
  }, [graphData]);

  const getFromGridGraphData = useMemo(() => {
    if (!graphData || !graphData.points) return [];

    return graphData.points.map((p) => {
      return Number(Helper.roundTo2(p.gridImportWh / 1000));
    });
  }, [graphData]);

  const getToGridGraphData = useMemo(() => {
    if (!graphData || !graphData.points) return [];

    return graphData.points.map((p) => {
      return Number(Helper.roundTo2(p.gridExportWh / 1000));
    });
  }, [graphData]);

  const getBatteryChargeGraphData = useMemo(() => {
    if (!graphData || !graphData.points) return [];

    return graphData.points.map((p) => {
      return Number(Helper.roundTo2(p.batteryChargeWh / 1000));
    });
  }, [graphData]);

  const getBatteryDischargeGraphData = useMemo(() => {
    if (!graphData || !graphData.points) return [];

    return graphData.points.map((p) => {
      return Number(Helper.roundTo2(p.batteryDischargeWh / 1000));
    });
  }, [graphData]);

  const xLabelCallback = useMemo(() => {
    if (!getGraphTimeStamps || getGraphTimeStamps.length === 0) return () => "";

    const first = getGraphTimeStamps[0];
    const last = getGraphTimeStamps[getGraphTimeStamps.length - 1];
    const duration = last - first;

    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    const oneWeek = 7 * oneDay;
    const oneMonth = 30 * oneDay;
    const oneYear = 365 * oneDay;

    let format = "HH:mm";

    if (duration <= oneHour) format = "HH:mm:ss";
    else if (duration <= 6 * oneHour) format = "HH:mm";
    else if (duration <= oneDay) format = "HH:mm";
    else if (duration <= oneWeek) format = "dd MMM";
    else if (duration <= oneMonth) format = "dd MMM";
    else if (duration <= oneYear) format = "dd MMM";
    else format = "MMM yyyy";

    return (_value: unknown, index: number) => {
      const ts = getGraphTimeStamps[index];
      if (!ts) return "";
      return DateTime.fromMillis(ts).setZone(zoneId).toFormat(format);
    };
  }, [getGraphTimeStamps, zoneId]);

  const titleCallback = useMemo(() => {
    if (!getGraphTimeStamps || getGraphTimeStamps.length === 0) return () => "";

    const first = getGraphTimeStamps[0];
    const last = getGraphTimeStamps[getGraphTimeStamps.length - 1];
    const duration = last - first;

    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    const oneWeek = 7 * oneDay;
    const oneMonth = 30 * oneDay;
    const oneYear = 365 * oneDay;

    let mode: "hour" | "day" | "month" | "year" = "hour";

    if (duration <= oneHour) mode = "hour";
    else if (duration <= 6 * oneHour) mode = "hour";
    else if (duration <= oneDay) mode = "hour";
    else if (duration <= oneWeek) mode = "day";
    else if (duration <= oneMonth) mode = "day";
    else if (duration <= oneYear) mode = "month";
    else mode = "year";

    return (tooltipItems: any[]) => {
      if (!tooltipItems || tooltipItems.length === 0) return "";
      const index = tooltipItems[0].dataIndex;
      const ts = getGraphTimeStamps[index];
      if (!ts) return "";

      const dt = DateTime.fromMillis(ts).setZone(zoneId);

      if (mode === "hour") {
        return dt.toFormat("dd MMM yyyy, HH:mm");
      } else if (mode === "day") {
        return dt.toFormat("dd MMM yyyy");
      } else if (mode === "month") {
        return dt.toFormat("MMM yyyy");
      } else {
        return dt.toFormat("yyyy");
      }
    };
  }, [getGraphTimeStamps, zoneId]);

  const xAxisLabel = useMemo(() => {
    if (!getGraphTimeStamps || getGraphTimeStamps.length === 0) return "";

    const first = getGraphTimeStamps[0];
    const last = getGraphTimeStamps[getGraphTimeStamps.length - 1];
    const duration = last - first;

    const oneHour = 60 * 60 * 1000;
    const oneDay = 24 * oneHour;
    const oneMonth = 30 * oneDay;
    const oneYear = 365 * oneDay;

    if (duration <= oneDay) return "Hour";
    if (duration <= oneMonth) return "Day";
    if (duration <= oneYear) return "Month";
    return "Year";
  }, [getGraphTimeStamps]);

  const getDateDifference = () => {
    return endDate.getTime() - startDate.getTime();
  };

  return (
    <div className="position-relative">
      {!isHideAlerts &&
        solarPlant?.solarPlantAlerts &&
        solarPlant?.solarPlantAlerts.length > 0 &&
        solarPlant?.plantStatus === EPlantStatus.FAULT && (
          <Row className="mb-4">
            <Col>
              <AppBannerWithIcon
                content={
                  <>
                    {solarPlant?.solarPlantAlerts.map((alert, index) => {
                      return (
                        <Row key={index}>
                          <Col className="col-auto text-white font-weight-400 font-size-12 pe-0">
                            {alert.alarmCode}&nbsp;&nbsp;&nbsp;|
                          </Col>
                          <Col className="text-white font-weight-400 font-size-12">
                            {alert.alarmMessage}
                          </Col>
                        </Row>
                      );
                    })}
                  </>
                }
                bannerVariant="red"
                icon="emergency_home"
                iconVariant="#FFFFFF"
                iconSize={18}
                button={
                  <>
                    <MaterialIcon
                      icon="close"
                      color="#ffffff"
                      size={15}
                      onClick={() => setIsHideAlerts(true)}
                      className="cursor-pointer"
                    />
                  </>
                }
              />
            </Col>
          </Row>
        )}
      {solarPlant?.serviceError && !isHideAlerts ? (
        <Row className="mb-4">
          <Col>
            <AppBannerWithIcon
              content={
                <Row>
                  <Col className="text-white font-weight-400 font-size-12">
                    API is temporarily down. Showing the last recorded data
                    until live updates resume.
                  </Col>
                </Row>
              }
              bannerVariant="red"
              icon="emergency_home"
              iconVariant="#FFFFFF"
              iconSize={18}
              button={
                <>
                  <MaterialIcon
                    icon="close"
                    color="#ffffff"
                    size={15}
                    onClick={() => setIsHideAlerts(true)}
                    className="cursor-pointer"
                  />
                </>
              }
            />
          </Col>
        </Row>
      ) : (
        <></>
      )}
      <Row>
        <Col className={`${styles.energyFlow} col-12 col-xxl-9`}>
          <GroWattEnergyFlow
            storagePowerData={storagePowerData}
            plantStatus={solarPlant.plantStatus}
          />
        </Col>
        <Col className="col-xxl-3 mt-4 mt-xxl-0">
          {(solarPlant?.address || solarPlant?.address1) &&
          solarPlant?.latitude &&
          solarPlant?.longitude ? (
            <Row className="mx-0">
              <Col className={`${styles.infoCardContainer} p-3`}>
                {(solarPlant?.address || solarPlant?.address1) && (
                  <Row className="mb-3">
                    <Col className="text-dark font-weight-500 font-size-16">
                      {solarPlant?.address || solarPlant?.address1}
                    </Col>
                  </Row>
                )}
                {solarPlant?.latitude && solarPlant?.longitude ? (
                  <>
                    <Row
                      className={`${styles.todayBackground} align-items-center mx-0 p-3`}
                    >
                      <Col>
                        <WetherInfo
                          longitude={Number(solarPlant?.longitude)}
                          latitude={Number(solarPlant?.latitude)}
                          hideOtherDays
                        />
                      </Col>
                    </Row>
                    {solarPlant?.latitude && solarPlant?.longitude && (
                      <Row className="mt-3">
                        <Col>
                          <LeafletMapDetails
                            locations={[
                              {
                                lat: Number(solarPlant?.latitude),
                                lng: Number(solarPlant?.longitude),
                                name: solarPlant?.name,
                              },
                            ]}
                            height={150}
                            hidePopup
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                ) : (
                  <></>
                )}
              </Col>
            </Row>
          ) : (
            <></>
          )}
        </Col>
      </Row>
      <Row
        className={`${styles.siteStaticContainer} py-4 px-2 mx-0 mt-4 position-relative`}
      >
        <Col>
          <Row className="g-4 row-cols-1 row-cols-sm-2 row-cols-xl-4">
            <Col>
              <SolarInfoCard
                title="Today Generation"
                value={`${
                  Number(Helper.roundTo2(Number(solarPlant?.todayEnergy))) ?? 0
                } kWh`}
                icon="solar_power"
                iconColor="#D08700"
                backgroundColor="#F7F8EE"
              />
            </Col>
            <Col>
              <SolarInfoCard
                title="Monthly Generation"
                value={`${
                  Number(Helper.roundTo2(Number(solarPlant?.monthlyEnergy))) ??
                  0
                } kWh`}
                icon="solar_power"
                iconColor="#D08700"
                backgroundColor="#F7F8EE"
              />
            </Col>
            <Col>
              <SolarInfoCard
                title="Yearly Generation"
                value={`${solarPlant?.yearlyEnergy ?? 0} kWh`}
                icon="solar_power"
                iconColor="#D08700"
                backgroundColor="#F7F8EE"
              />
            </Col>
            <Col>
              <SolarInfoCard
                title="Total Generation"
                value={`${solarPlant?.totalEnergy ?? 0} kWh`}
                icon="solar_power"
                iconColor="#D08700"
                backgroundColor="#F7F8EE"
              />
            </Col>
          </Row>
          <div className="container-white position-relative mt-4">
            <Row className="align-items-center justify-content-between">
              <Col className="col-auto text-dark font-weight-500 font-size-20">
                Site Statistics
              </Col>
              <Col className="col-12 col-lg-9 mt-1 mt-sm-0">
                <Row className="justify-content-end">
                  <Col className="col-12 col-sm-6 col-lg-6 col-xl-5">
                    <AppSelect
                      options={graphTypeOptions}
                      selectedValue={selectedGraphType}
                      onChangeOption={(selectOption) => {
                        setSelectedGraphType(selectOption);
                      }}
                    />
                  </Col>
                  <Col className="col-12 col-sm-6 col-lg-auto mt-2 mt-sm-0">
                    <ArrowIconsButton
                      text={
                        selectChartFilter.isFilterCustom
                          ? `${moment(
                              new Date(selectChartFilter.startDate),
                            ).format("YYYY/MM/DD HH:mm")} to ${moment(
                              new Date(selectChartFilter.endDate),
                            ).format("YYYY/MM/DD HH:mm")}`
                          : String(quickFilter.label)
                      }
                      onClick={() => setShowSiteViewDashboardFilters(true)}
                      onLeftIconClick={() => {
                        const diff = getDateDifference();
                        dispatch(
                          setSiteChartFilter({
                            startDate: new Date(
                              startDate.getTime() - diff,
                            ).toISOString(),
                            endDate: new Date(
                              endDate.getTime() - diff,
                            ).toISOString(),
                            viewType: selectChartFilter.viewType,
                            isFilterCustom: true,
                          }),
                        );
                      }}
                      onRightIconClick={() => {
                        const diff = getDateDifference();
                        dispatch(
                          setSiteChartFilter({
                            startDate: new Date(
                              startDate.getTime() + diff,
                            ).toISOString(),
                            endDate: new Date(
                              endDate.getTime() + diff,
                            ).toISOString(),
                            viewType: selectChartFilter.viewType,
                            isFilterCustom: true,
                          }),
                        );
                      }}
                      showArrowIcon={selectChartFilter.isFilterCustom}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
            {selectedGraphType.value === ESolarPlantGraphType.LIVE_FEED ? (
              <MultipleChartType
                borderColor1="#E45151"
                borderColor2="#D08700"
                borderColor3="#62A1C9"
                borderColor4="#3E6E8C"
                borderColor5="#5E45B8"
                backgroundColor1="#E45151"
                backgroundColor2="#D08700"
                backgroundColor3="#62A1C9"
                backgroundColor4="#3E6E8C"
                backgroundColor5="#FFFFFF14"
                label1="Consumption"
                label2="Solar"
                label3="From Grid"
                label4="To Grid"
                label5="Battery"
                data1={getConsumptionGraphData}
                data2={getSolarGraphData}
                data3={getFromGridGraphData}
                data4={getToGridGraphData}
                data5={getBatteryDischargeGraphData}
                labels={getGraphTimeStamps}
                yAxisUnitLeftSide="kWh"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const datasetType = tooltipItem.dataset.type;
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  const unit = datasetType === "bar" ? "kWh" : "%";
                  return `${label}: ${value} ${unit}`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                isChartDataAvailable={
                  getConsumptionGraphData.length > 0 ||
                  getSolarGraphData.length > 0 ||
                  getFromGridGraphData.length > 0 ||
                  getToGridGraphData.length > 0 ||
                  getBatteryDischargeGraphData.length > 0
                }
              />
            ) : selectedGraphType.value === ESolarPlantGraphType.CONSUMPTION ? (
              <BarChart
                borderColor1="#E45151"
                borderColor2="#62A1C9"
                borderColor3="#5E45B8"
                backgroundColor1="#E45151"
                backgroundColor2="#62A1C9"
                backgroundColor3="#5E45B8"
                dataLabel1="Consumption"
                dataLabel2="From Grid"
                dataLabel3="Battery"
                data1={getConsumptionGraphData}
                data2={getFromGridGraphData}
                data3={getBatteryDischargeGraphData}
                labels={getGraphTimeStamps}
                yAxesUnit="kWh"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  return `${label}: ${value} kWh`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                hasThirdDataSet
                stacked
                isDisplayLegend
                isChartDataAvailable={
                  getConsumptionGraphData.length > 0 ||
                  getFromGridGraphData.length > 0 ||
                  getBatteryDischargeGraphData.length > 0
                }
              />
            ) : selectedGraphType.value === ESolarPlantGraphType.SOLAR_YIELD ? (
              <BarChart
                borderColor1="#D08700"
                borderColor2="#3E6E8C"
                borderColor3="#5E45B8"
                backgroundColor1="#D08700"
                backgroundColor2="#3E6E8C"
                backgroundColor3="#5E45B8"
                dataLabel1="Solar"
                dataLabel2="To Grid"
                dataLabel3="Battery"
                data1={getSolarGraphData}
                data2={getToGridGraphData}
                data3={getBatteryChargeGraphData}
                labels={getGraphTimeStamps}
                yAxesUnit="kWh"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  return `${label}: ${value} kWh`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                hasThirdDataSet
                stacked
                isDisplayLegend
                isChartDataAvailable={
                  getSolarGraphData.length > 0 ||
                  getToGridGraphData.length > 0 ||
                  getBatteryChargeGraphData.length > 0
                }
              />
            ) : (
              <BarChart
                borderColor1="#62A1C9"
                borderColor2="#3E6E8C"
                backgroundColor1="#62A1C9"
                backgroundColor2="#3E6E8C"
                dataLabel1="From Grid"
                dataLabel2="To Grid"
                data1={getFromGridGraphData}
                data2={getToGridGraphData}
                labels={getGraphTimeStamps}
                yAxesUnit="kWh"
                xAxisLabel={xAxisLabel}
                labelCallback={(tooltipItem: any) => {
                  const label = tooltipItem.dataset.label || "";
                  const value = Number(
                    Helper.roundTo2(Number(tooltipItem.raw)),
                  );

                  return `${label}: ${value} kWh`;
                }}
                titleCallback={titleCallback}
                xLabelCallback={xLabelCallback}
                hasSecondDataSet
                stacked
                isDisplayLegend
                isChartDataAvailable={
                  getFromGridGraphData.length > 0 ||
                  getToGridGraphData.length > 0
                }
              />
            )}
            <ChartLoadingOverlay show={isFetchingCustomerPlantGraphs} />
          </div>
        </Col>
      </Row>
      <VrmSiteDashboardFilters
        show={showSiteViewDashboardFilters}
        onClose={() => setShowSiteViewDashboardFilters(false)}
        setQuickFilter={setQuickFilter}
        quickFilter={quickFilter}
      />
      <SpinnerModal
        show={isFetchingCustomerPlantStorageData && isFirstStorageLoad}
      />
    </div>
  );
};

export default GroWattSiteDashboard;
